/**
 * Wykonanie kontra rozkład (Etap 42, ADR-0043) razem z rejestrem czasu wolnego
 * w zamian za nadgodziny (art. 151(2) k.p., Etap 43, ADR-0044).
 *
 * Ewidencja z Etapu 39 mówi, co się stało, rozkład z Etapu 41 — co miało się
 * stać. Dopiero jedno z drugim odpowiada na pytanie, którego żadne z nich nie
 * rozstrzyga osobno: ile z przepracowanych godzin jest nadgodzinami
 * (art. 151 § 1 k.p.), które z nich mają dodatek 100 % (art. 151(1) § 1)
 * i ile godzin wypadło w porze nocnej z okna firmy (art. 151(7) § 1).
 *
 * Czas wolny jest w tej samej karcie, bo dotyczy tych samych godzin: oddany
 * zdejmuje dodatek (§ 3), a udzielony przez pracodawcę po okresie
 * rozliczeniowym — nie zdejmuje go wcale (§ 2).
 *
 * Karta nie liczy niczego sama — pokazuje liczby z tego samego silnika, którym
 * liczy naliczenie płac.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Select,
  Spinner,
  useToast,
} from '@k1p4/ui'
import { formatDate, formatPLN } from '@k1p4/i18n'
import { grToZloty, zlotyToGr } from '../../components/money'
import { FULL_TIME_FTE_BP, workingTimeFraction } from '@k1p4/shared'
import { api, ApiError, apiMonth } from '../../api/client'
import styles from './FamilyMembers.module.css'

export interface TimeReviewProps {
  employeeId: string
  /** Zapis czasu wolnego wymaga uprawnienia do czasu pracy albo kadr. */
  canEdit: boolean
  /**
   * Ryczałt to pieniądze — pola ryczałtu i pracy poza zakładem dostaje tylko
   * rola z zakresem wynagrodzeń (przegląd Fali 4, fala 7b, grupa 9).
   */
  canSetPay?: boolean
}

/** Szkic formularza granic roku: godziny i kwoty tak, jak je wpisuje człowiek. */
interface LimitsDraft {
  annualLimitHours: string
  rescueHours: string
  managingEmployee: boolean
  outsideWorkplace: boolean
  overtimeLumpSum: string
  nightLumpSum: string
  note: string
}

function currentMonth(): string {
  return apiMonth()
}

/** Wpis rejestru dyżurów (Etap 50): kiedy, gdzie, jak długo i ile pracy. */
interface DutyDraft {
  date: string
  place: 'zaklad' | 'dom'
  hours: string
  workedHours: string
  timeOffHours: string
  note: string
}

function emptyDutyDraft(): DutyDraft {
  return { date: '', place: 'zaklad', hours: '', workedHours: '', timeOffHours: '', note: '' }
}

/** Wpis rejestru dni wolnych w zamian (Etap 49): dzień pracy i dzień wolny. */
interface DayOffDraft {
  workDate: string
  grantedOn: string
  note: string
}

interface TimeOffDraft {
  mode: 'wniosek' | 'pracodawca'
  grantedFrom: string
  grantedTo: string
  grantedHours: string
  note: string
}

function emptyDraft(): TimeOffDraft {
  return { mode: 'wniosek', grantedFrom: '', grantedTo: '', grantedHours: '', note: '' }
}

export function TimeReview({ employeeId, canEdit, canSetPay = false }: TimeReviewProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = {
    month: useId(),
    mode: useId(),
    from: useId(),
    to: useId(),
    hours: useId(),
    note: useId(),
    workDate: useId(),
    grantedOn: useId(),
    dayOffNote: useId(),
    dutyDate: useId(),
    dutyPlace: useId(),
    dutyHours: useId(),
    dutyWorked: useId(),
    dutyTimeOff: useId(),
    dutyNote: useId(),
    limitAnnual: useId(),
    limitRescue: useId(),
    limitOvertimeLump: useId(),
    limitNightLump: useId(),
    limitNote: useId(),
  }

  const [month, setMonth] = useState(currentMonth)
  const [draft, setDraft] = useState<TimeOffDraft | null>(null)
  const [dayOffDraft, setDayOffDraft] = useState<DayOffDraft | null>(null)
  const [dutyDraft, setDutyDraft] = useState<DutyDraft | null>(null)
  const [error, setError] = useState<string | null>(null)
  /* Formularz granic i ryczałtów (art. 151 § 3–4, art. 151(1) § 4, fala 7b, grupa 9). */
  const [limitsDraft, setLimitsDraft] = useState<LimitsDraft | null>(null)

  const review = useQuery({
    queryKey: ['time-review', employeeId, month],
    queryFn: () => api.timeReview(employeeId, month),
  })
  const timeOff = useQuery({
    queryKey: ['overtime-time-off', employeeId, month],
    queryFn: () => api.overtimeTimeOff(employeeId, month),
  })
  /* Rejestr dni wolnych w zamian za niedzielę i święto (Etap 49, ADR-0050). */
  const daysOff = useQuery({
    queryKey: ['substitute-days-off', employeeId, month],
    queryFn: () => api.substituteDaysOff(employeeId, month),
  })
  /* Rejestr dyżurów (art. 151(5) k.p., Etap 50, ADR-0051). */
  const duty = useQuery({
    queryKey: ['duty-shifts', employeeId, month],
    queryFn: () => api.dutyShifts(employeeId, month),
  })

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['time-review', employeeId, month] })
    void queryClient.invalidateQueries({ queryKey: ['overtime-time-off', employeeId, month] })
    void queryClient.invalidateQueries({ queryKey: ['substitute-days-off', employeeId, month] })
    void queryClient.invalidateQueries({ queryKey: ['duty-shifts', employeeId, month] })
  }

  const grant = useMutation({
    mutationFn: (values: TimeOffDraft) =>
      api.grantOvertimeTimeOff(employeeId, {
        month,
        mode: values.mode,
        grantedFrom: values.grantedFrom,
        grantedTo: values.grantedTo === '' ? values.grantedFrom : values.grantedTo,
        grantedHours: Number(values.grantedHours.replace(',', '.')),
        ...(values.note.trim() === '' ? {} : { note: values.note.trim() }),
      }),
    onSuccess: () => {
      toast.show(t('timeOff.saved'), { tone: 'success' })
      setDraft(null)
      setError(null)
      refresh()
    },
    /* „Więcej, niż było nadgodzin" to odpowiedź serwera z liczbami — pokazujemy ją. */
    onError: (cause) =>
      setError(
        cause instanceof ApiError && cause.code === 'OVERTIME_EXCEEDED'
          ? cause.message
          : t('timeOff.errorGeneric'),
      ),
  })

  const revoke = useMutation({
    mutationFn: (entryId: string) => api.revokeOvertimeTimeOff(employeeId, entryId),
    onSuccess: () => {
      toast.show(t('timeOff.revoked'), { tone: 'success' })
      setError(null)
      refresh()
    },
    onError: () => setError(t('timeOff.errorGeneric')),
  })

  const grantDayOff = useMutation({
    mutationFn: (values: DayOffDraft) =>
      api.grantSubstituteDayOff(employeeId, {
        month,
        workDate: values.workDate,
        grantedOn: values.grantedOn,
        ...(values.note.trim() === '' ? {} : { note: values.note.trim() }),
      }),
    onSuccess: () => {
      toast.show(t('substituteDayOff.saved'), { tone: 'success' })
      setDayOffDraft(null)
      setError(null)
      refresh()
    },
    /*
     * „Ten dzień nie wymaga dnia wolnego" i „brak godzin w ewidencji" to
     * odpowiedzi serwera z powodem — pokazujemy je, zamiast zgadywać.
     */
    onError: (cause) =>
      setError(
        cause instanceof ApiError &&
          (cause.code === 'DAY_NOT_ELIGIBLE' || cause.code === 'DAY_NOT_WORKED')
          ? cause.message
          : t('substituteDayOff.errorGeneric'),
      ),
  })

  const revokeDayOff = useMutation({
    mutationFn: (entryId: string) => api.revokeSubstituteDayOff(employeeId, entryId),
    onSuccess: () => {
      toast.show(t('substituteDayOff.revoked'), { tone: 'success' })
      setError(null)
      refresh()
    },
    onError: () => setError(t('substituteDayOff.errorGeneric')),
  })

  const recordDuty = useMutation({
    mutationFn: (values: DutyDraft) =>
      api.recordDutyShift(employeeId, {
        month,
        date: values.date,
        place: values.place,
        hours: Number(values.hours.replace(',', '.')),
        ...(values.workedHours.trim() === ''
          ? {}
          : { workedHours: Number(values.workedHours.replace(',', '.')) }),
        ...(values.timeOffHours.trim() === ''
          ? {}
          : { timeOffHours: Number(values.timeOffHours.replace(',', '.')) }),
        ...(values.note.trim() === '' ? {} : { note: values.note.trim() }),
      }),
    onSuccess: () => {
      toast.show(t('duty.saved'), { tone: 'success' })
      setDutyDraft(null)
      setError(null)
      refresh()
    },
    /* „Praca dłuższa od dyżuru" i „dyżur w domu" to odpowiedzi serwera z powodem. */
    onError: (cause) =>
      setError(
        cause instanceof ApiError && cause.code === 'PERIOD_CLOSED'
          ? t('duty.errorPeriodClosed')
          : cause instanceof ApiError &&
              (cause.code === 'VALIDATION_ERROR' || cause.code === 'HOME_DUTY')
            ? cause.message
            : t('duty.errorGeneric'),
      ),
  })

  /*
   * Zapis granic roku wysyła KOMPLET pól — trasa zapisuje cały wiersz roku,
   * więc pominięte pole (np. notatka) wróciłoby do wartości domyślnej.
   */
  const saveLimits = useMutation({
    mutationFn: (values: LimitsDraft) =>
      api.saveOvertimeLimits(employeeId, {
        year: Number(month.slice(0, 4)),
        annualLimitHours: Number(values.annualLimitHours),
        rescueHours: Number(values.rescueHours.replace(',', '.') || '0'),
        managingEmployee: values.managingEmployee,
        note: values.note.trim() === '' ? null : values.note.trim(),
        ...(canSetPay
          ? {
              outsideWorkplace: values.outsideWorkplace,
              overtimeLumpSumGr: values.outsideWorkplace ? zlotyToGr(values.overtimeLumpSum) : null,
              nightLumpSumGr: values.outsideWorkplace ? zlotyToGr(values.nightLumpSum) : null,
            }
          : {}),
      }),
    onSuccess: () => {
      toast.show(t('overtimeLimits.form.saved'), { tone: 'success' })
      setLimitsDraft(null)
      setError(null)
      refresh()
    },
    /* „Ryczałt tylko przy pracy poza zakładem" to odpowiedź serwera z przepisem. */
    onError: (cause) =>
      setError(
        cause instanceof ApiError &&
          (cause.code === 'LUMP_SUM_NOT_ALLOWED' || cause.code === 'VALIDATION_ERROR')
          ? cause.message
          : t('overtimeLimits.form.errorGeneric'),
      ),
  })

  const revokeDuty = useMutation({
    mutationFn: (entryId: string) => api.revokeDutyShift(employeeId, entryId),
    onSuccess: () => {
      toast.show(t('duty.revoked'), { tone: 'success' })
      setError(null)
      refresh()
    },
    onError: (cause) =>
      setError(
        cause instanceof ApiError && cause.code === 'PERIOD_CLOSED'
          ? t('duty.errorPeriodClosed')
          : t('duty.errorGeneric'),
      ),
  })

  const data = review.data
  const off = timeOff.data
  const restByDay = new Map((data?.restBreaches ?? []).map((breach) => [breach.date, breach.restHours]))
  /* Dni, za które dzień wolny się należy, a jeszcze go nie udzielono w terminie. */
  const pendingDays = [
    ...(daysOff.data?.sundayDays ?? []),
    ...(daysOff.data?.freeDayWork ?? []),
  ].filter((day) => !day.inTerm)

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('timeReview.title')}</h3>
      </div>
      <p className={styles.note}>{t('timeReview.lead')}</p>

      <div className={styles.formGrid}>
        <FormField label={t('timeReview.month')} htmlFor={ids.month}>
          <Input
            id={ids.month}
            type="month"
            value={month}
            onChange={(event) => {
              setDraft(null)
              setMonth(event.target.value)
            }}
          />
        </FormField>
      </div>

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {error}
        </InfoBanner>
      ) : null}

      {review.isPending ? (
        <Spinner label={t('timeReview.loading')} />
      ) : data === undefined ? (
        <EmptyState title={t('common.error')} description={t('common.retry')} />
      ) : (
        <>
          <InfoBanner tone="info" className={styles.banner}>
            {t('timeReview.summary', {
              worked: data.workedHours,
              planned: data.plannedHours,
              norm: data.normHours,
            })}
            {' '}
            {/*
              Okres rozliczeniowy mówi, KIEDY rozlicza się przeciętna norma
              tygodniowa (art. 129 § 1). Przy okresie miesięcznym to ten miesiąc.
            */}
            {data.period.months > 1
              ? t('timeReview.period', {
                  from: data.period.from,
                  to: data.period.to,
                  months: data.period.months,
                })
              : ''}
            {data.normHoursAfterAbsence !== data.normHours
              ? ` ${t('timeReview.normAfterAbsence', { hours: data.normHoursAfterAbsence })}`
              : ''}
            {/*
              Wymiar PRACOWNIKA z etatu (fala 7b, #25). Próg nadgodzin
              średniotygodniowych zostaje przy wymiarze okresu (decyzja
              właściciela 2026-09-28), więc pokazujemy obie liczby.
            */}
            {data.laborCode && data.fteBp !== FULL_TIME_FTE_BP
              ? ` ${t('timeReview.employeeNorm', {
                  fte: workingTimeFraction(data.fteBp / FULL_TIME_FTE_BP),
                  hours: data.employeeNormHours,
                })}${
                  data.employeeNormHoursAfterAbsence !== data.employeeNormHours
                    ? ` ${t('timeReview.employeeNormAfterAbsence', {
                        hours: data.employeeNormHoursAfterAbsence,
                      })}`
                    : ''
                }`
              : ''}
            {data.laborCode ? '' : ` ${t('timeReview.notLaborCode')}`}
            <ul>
              <li>{t('timeReview.overtimeDay', { hours: data.overtimeDayRateHours })}</li>
              <li>{t('timeReview.overtimeNight', { hours: data.overtimeNightRateHours })}</li>
              {/*
                Nadgodziny średniotygodniowe płacimy tylko z rozkładem: bez niego
                „nadwyżka nad wymiarem" mówi o kompletności ewidencji, nie o pracy.
              */}
              {data.periodOvertimeHours > 0 ? (
                <li>
                  {t(
                    data.scheduleMissing
                      ? 'timeReview.overtimeWeeklyIgnored'
                      : 'timeReview.overtimeWeekly',
                    { hours: data.periodOvertimeHours },
                  )}
                </li>
              ) : data.period.months > 1 && !data.period.isLast ? (
                <li>{t('timeReview.periodOpen')}</li>
              ) : null}
              {data.timeOffCoveredHours > 0 ? (
                <li>{t('timeReview.timeOffCovered', { hours: data.timeOffCoveredHours })}</li>
              ) : null}
              <li>
                {t('timeReview.night', {
                  hours: data.nightHours,
                  from: data.window.startsAt,
                  to: data.window.endsAt,
                })}
              </li>
            </ul>
          </InfoBanner>

          {/*
            W systemie zadaniowym godzin pracy nie ewidencjonuje się
            (art. 149 § 2 k.p.), więc brak rozkładu nie jest brakiem — mówimy to
            wprost, zamiast pokazywać ostrzeżenie o rozkładzie.
          */}
          {/*
            Powód z art. 149 § 2 wybiera tekst (fala 7b, grupa 81): przy
            ryczałcie i u zarządzającego rozkład obowiązuje, więc jego brak
            dalej jest ostrzeżeniem — tylko w zadaniowym rozkładu nie ma.
          */}
          {data.hoursNotTrackedReason !== null ? (
            <InfoBanner tone="info" className={styles.banner}>
              {t(`timeReview.hoursNotTracked.${data.hoursNotTrackedReason}`)}
            </InfoBanner>
          ) : null}
          {data.hoursNotTrackedReason === 'zadaniowy' ? null : data.scheduleMissing ? (
            <InfoBanner tone="warning" className={styles.banner}>
              {t('timeReview.scheduleMissing')}
            </InfoBanner>
          ) : null}
          {data.clockMissingDays > 0 ? (
            <InfoBanner tone="warning" className={styles.banner}>
              {t('timeReview.clockMissing', { days: data.clockMissingDays })}
            </InfoBanner>
          ) : null}
          {/* Odpoczynek dobowy z ewidencji (art. 132 § 1, fala 7b, grupa 175). */}
          {data.restBreaches.length > 0 ? (
            <InfoBanner tone="warning" className={styles.banner}>
              {t('timeReview.restBreaches', {
                count: data.restBreaches.length,
                hours: Math.min(...data.restBreaches.map((breach) => breach.restHours)),
              })}
            </InfoBanner>
          ) : null}
          {data.timeOffLateHours > 0 ? (
            <InfoBanner tone="warning" className={styles.banner}>
              {t('timeOff.lateWarning', { hours: data.timeOffLateHours })}
            </InfoBanner>
          ) : null}

          {/*
            Granice pracy nadliczbowej (art. 151 § 3 i art. 131 § 1 k.p.).
            Przekroczenie nie odbiera zapłaty — jest wykroczeniem, więc mówimy
            o nim tam, gdzie kadry patrzą na godziny.
          */}
          {data.limits !== null ? (
            <InfoBanner
              tone={
                data.limits.annual.exceededHours > 0 || data.limits.periodExceededHours > 0
                  ? 'danger'
                  : 'info'
              }
              className={styles.banner}
            >
              {t('overtimeLimits.annual', {
                used: data.limits.annual.countedHours,
                limit: data.limits.annualLimitHours,
                remaining: data.limits.annual.remainingHours,
              })}
              {data.limits.rescueHours > 0
                ? ` ${t('overtimeLimits.rescue', { hours: data.limits.rescueHours })}`
                : ''}
              {data.limits.annual.exceededHours > 0
                ? ` ${t('overtimeLimits.annualExceeded', {
                    hours: data.limits.annual.exceededHours,
                  })}`
                : ''}
              {' '}
              {data.limits.managingEmployee
                ? t('overtimeLimits.managing')
                : data.limits.periodClosed
                  ? t(
                      data.limits.periodExceededHours > 0
                        ? 'overtimeLimits.weeklyExceeded'
                        : 'overtimeLimits.weekly',
                      {
                        average: data.limits.averageWeeklyHours,
                        hours: data.limits.periodExceededHours,
                      },
                    )
                  : t('overtimeLimits.weeklyOpen')}
              {canEdit && limitsDraft === null ? (
                <>
                  {' '}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setLimitsDraft({
                        annualLimitHours: String(data.limits!.annualLimitHours),
                        rescueHours: String(data.limits!.rescueHours),
                        managingEmployee: data.limits!.managingEmployee,
                        outsideWorkplace: data.lumpSum.outsideWorkplace,
                        overtimeLumpSum: grToZloty(data.lumpSum.overtimeLumpSumGr),
                        nightLumpSum: grToZloty(data.lumpSum.nightLumpSumGr),
                        note: data.limits!.note ?? '',
                      })
                    }
                  >
                    {t('overtimeLimits.form.open')}
                  </Button>
                </>
              ) : null}
            </InfoBanner>
          ) : null}

          {limitsDraft !== null ? (
            <Card pad="md" className={styles.banner}>
              <h4 className={styles.title}>{t('overtimeLimits.form.title', { year: month.slice(0, 4) })}</h4>
              <div className={styles.formGrid}>
              <FormField
                label={t('overtimeLimits.form.annualLimit')}
                htmlFor={ids.limitAnnual}
                hint={t('overtimeLimits.form.annualLimitHint')}
              >
                <Input
                  id={ids.limitAnnual}
                  inputMode="numeric"
                  value={limitsDraft.annualLimitHours}
                  onChange={(event) => setLimitsDraft({ ...limitsDraft, annualLimitHours: event.target.value })}
                />
              </FormField>
              <FormField
                label={t('overtimeLimits.form.rescue')}
                htmlFor={ids.limitRescue}
                hint={t('overtimeLimits.form.rescueHint')}
              >
                <Input
                  id={ids.limitRescue}
                  inputMode="decimal"
                  value={limitsDraft.rescueHours}
                  onChange={(event) => setLimitsDraft({ ...limitsDraft, rescueHours: event.target.value })}
                />
              </FormField>
              <Checkbox
                checked={limitsDraft.managingEmployee}
                onChange={(event) => setLimitsDraft({ ...limitsDraft, managingEmployee: event.target.checked })}
                label={t('overtimeLimits.form.managing')}
              />
              {canSetPay ? (
                <>
                  <Checkbox
                    checked={limitsDraft.outsideWorkplace}
                    onChange={(event) =>
                      setLimitsDraft({ ...limitsDraft, outsideWorkplace: event.target.checked })
                    }
                    label={t('overtimeLimits.form.outsideWorkplace')}
                  />
                  <FormField label={t('overtimeLimits.form.overtimeLumpSum')} htmlFor={ids.limitOvertimeLump}>
                    <Input
                      id={ids.limitOvertimeLump}
                      inputMode="decimal"
                      disabled={!limitsDraft.outsideWorkplace}
                      value={limitsDraft.overtimeLumpSum}
                      onChange={(event) => setLimitsDraft({ ...limitsDraft, overtimeLumpSum: event.target.value })}
                    />
                  </FormField>
                  <FormField label={t('overtimeLimits.form.nightLumpSum')} htmlFor={ids.limitNightLump}>
                    <Input
                      id={ids.limitNightLump}
                      inputMode="decimal"
                      disabled={!limitsDraft.outsideWorkplace}
                      value={limitsDraft.nightLumpSum}
                      onChange={(event) => setLimitsDraft({ ...limitsDraft, nightLumpSum: event.target.value })}
                    />
                  </FormField>
                </>
              ) : null}
              <FormField label={t('overtimeLimits.form.note')} htmlFor={ids.limitNote}>
                <Input
                  id={ids.limitNote}
                  value={limitsDraft.note}
                  onChange={(event) => setLimitsDraft({ ...limitsDraft, note: event.target.value })}
                />
              </FormField>
              </div>
              <div className={styles.actions}>
                <Button variant="secondary" onClick={() => setLimitsDraft(null)}>
                  {t('overtimeLimits.form.cancel')}
                </Button>
                <Button
                  disabled={saveLimits.isPending || !/^\d+$/.test(limitsDraft.annualLimitHours.trim())}
                  onClick={() => saveLimits.mutate(limitsDraft)}
                >
                  {saveLimits.isPending ? <Spinner label={t('overtimeLimits.form.saving')} /> : t('overtimeLimits.form.save')}
                </Button>
              </div>
            </Card>
          ) : null}

          {/*
            Ryczałt za nadgodziny albo za pracę w nocy (art. 151(1) § 4
            i art. 151(8) § 2 k.p., Etap 51). Zastępuje zapłatę, ale nie
            godziny: te zostają w ewidencji i w licznikach, a różnicę między
            ryczałtem a wyliczeniem pokazuje lista płac ostrzeżeniem.
          */}
          {data.lumpSum.overtimeLumpSumGr !== null || data.lumpSum.nightLumpSumGr !== null ? (
            <InfoBanner tone="info" className={styles.banner}>
              {data.lumpSum.overtimeLumpSumGr !== null
                ? t('lumpSum.overtime', {
                    amount: formatPLN(data.lumpSum.overtimeLumpSumGr),
                  })
                : ''}
              {data.lumpSum.overtimeLumpSumGr !== null && data.lumpSum.nightLumpSumGr !== null
                ? ' '
                : ''}
              {data.lumpSum.nightLumpSumGr !== null
                ? t('lumpSum.night', { amount: formatPLN(data.lumpSum.nightLumpSumGr) })
                : ''}
            </InfoBanner>
          ) : null}

          {data.days.length === 0 ? (
            <EmptyState title={t('timeReview.empty')} description={t('timeReview.emptyBody')} />
          ) : (
            <Card pad="flush" className={styles.list}>
              {data.days.map((day) => (
                <div key={day.date} className={styles.row}>
                  <span className={styles.rowName}>{formatDate(day.date, 'dot')}</span>
                  <span className={styles.rowMeta}>
                    {t('timeReview.dayHours', {
                      planned: day.plannedHours,
                      worked: day.workedHours,
                    })}
                  </span>
                  <span className={styles.rowMeta}>
                    {day.overtimeHours > 0
                      ? t(
                          day.overtimeRateBp === 10_000
                            ? 'timeReview.dayOvertime100'
                            : 'timeReview.dayOvertime50',
                          { hours: day.overtimeHours },
                        )
                      : day.plannedOff
                        ? t('timeReview.dayOff')
                        : ''}
                  </span>
                  <span className={styles.rowMeta}>
                    {day.nightHours > 0 ? t('timeReview.dayNight', { hours: day.nightHours }) : ''}
                    {restByDay.has(day.date)
                      ? ` ${t('timeReview.dayRest', { hours: restByDay.get(day.date) })}`
                      : ''}
                  </span>
                </div>
              ))}
            </Card>
          )}

          {/*
            Rejestr czasu wolnego w zamian (art. 151(2) k.p.). Zapisujemy FAKT
            udzielenia; ile nadgodzin pokrywa, wynika z przepisu — 1:1 na wniosek
            pracownika, 1,5:1 z inicjatywy pracodawcy.
          */}
          <div className={styles.head}>
            <h3 className={styles.title}>{t('timeOff.title')}</h3>
            {canEdit && off !== undefined && off.remainingOvertimeHours > 0 && draft === null ? (
              <Button size="sm" variant="secondary" onClick={() => setDraft(emptyDraft())}>
                {t('timeOff.add')}
              </Button>
            ) : null}
          </div>
          <p className={styles.note}>{t('timeOff.lead')}</p>

          {off === undefined ? null : (
            <>
              <p className={styles.note}>
                {t('timeOff.remaining', { hours: off.remainingOvertimeHours })}
              </p>
              {/*
                Zamknięty miesiąc nie zdejmie dodatku sam: trzeba go otworzyć
                ponownie z powodem w audycie i przeliczyć (Etap 13, ADR-0014).
              */}
              {/* Status listy płac to „zamknięta" z enumu — nie „closed" (fala 7b, grupa 136). */}
              {off.payrollStatus === 'zamknięta' && off.items.length > 0 ? (
                <InfoBanner tone="warning" className={styles.banner}>
                  {t('timeOff.payrollClosed')}
                </InfoBanner>
              ) : null}
              {off.timePeriodStatus === 'closed' ? (
                <InfoBanner tone="info" className={styles.banner}>
                  {t('timeOff.periodClosed')}
                </InfoBanner>
              ) : null}

              {off.items.length === 0 ? (
                <EmptyState title={t('timeOff.empty')} description={t('timeOff.emptyBody')} />
              ) : (
                <Card pad="flush" className={styles.list}>
                  {off.items.map((item) => (
                    <div key={item.id} className={styles.row}>
                      <span className={styles.rowName}>
                        {item.grantedFrom === item.grantedTo
                          ? formatDate(item.grantedFrom, 'dot')
                          : `${formatDate(item.grantedFrom, 'dot')}–${formatDate(item.grantedTo, 'dot')}`}
                      </span>
                      <span className={styles.rowMeta}>{t(`timeOff.modes.${item.mode}`)}</span>
                      <span className={styles.rowMeta}>
                        {t('timeOff.hours', {
                          granted: item.grantedHours,
                          covered: item.coveredHours,
                        })}
                      </span>
                      <span className={styles.rowMeta}>
                        {item.inPeriod ? '' : t('timeOff.late')}
                      </span>
                      {canEdit ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={revoke.isPending}
                          onClick={() => revoke.mutate(item.id)}
                        >
                          {t('timeOff.revoke')}
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </Card>
              )}
            </>
          )}

          {draft !== null ? (
            <>
              <div className={styles.formGrid}>
                <FormField label={t('timeOff.fields.mode')} htmlFor={ids.mode}>
                  <Select
                    id={ids.mode}
                    value={draft.mode}
                    onChange={(event) =>
                      setDraft({ ...draft, mode: event.target.value as TimeOffDraft['mode'] })
                    }
                  >
                    <option value="wniosek">{t('timeOff.modes.wniosek')}</option>
                    <option value="pracodawca">{t('timeOff.modes.pracodawca')}</option>
                  </Select>
                </FormField>
                <FormField label={t('timeOff.fields.from')} htmlFor={ids.from}>
                  <Input
                    id={ids.from}
                    type="date"
                    value={draft.grantedFrom}
                    onChange={(event) => setDraft({ ...draft, grantedFrom: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('timeOff.fields.to')}
                  htmlFor={ids.to}
                  hint={t('timeOff.fields.toHint')}
                >
                  <Input
                    id={ids.to}
                    type="date"
                    value={draft.grantedTo}
                    onChange={(event) => setDraft({ ...draft, grantedTo: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('timeOff.fields.hours')}
                  htmlFor={ids.hours}
                  hint={t('timeOff.fields.hoursHint')}
                >
                  <Input
                    id={ids.hours}
                    value={draft.grantedHours}
                    onChange={(event) => setDraft({ ...draft, grantedHours: event.target.value })}
                  />
                </FormField>
                <FormField label={t('timeOff.fields.note')} htmlFor={ids.note}>
                  <Input
                    id={ids.note}
                    value={draft.note}
                    onChange={(event) => setDraft({ ...draft, note: event.target.value })}
                  />
                </FormField>
              </div>
              <div className={styles.actions}>
                <Button variant="secondary" onClick={() => setDraft(null)}>
                  {t('timeOff.cancel')}
                </Button>
                <Button
                  disabled={
                    grant.isPending ||
                    draft.grantedFrom === '' ||
                    Number(draft.grantedHours.replace(',', '.')) <= 0
                  }
                  onClick={() => {
                    setError(null)
                    grant.mutate(draft)
                  }}
                >
                  {grant.isPending ? <Spinner label={t('timeOff.saving')} /> : t('timeOff.save')}
                </Button>
              </div>
            </>
          ) : null}

          {/*
            Rejestr dni wolnych w zamian za pracę w niedzielę, święto i w dniu
            wolnym z rozkładu (art. 151(11) i art. 151(3) k.p., Etap 49).
            Powód i termin biorą się z faktów: formularz pyta tylko o dzień
            pracy i o dzień wolny, a przepis mówi, do kiedy wolno go oddać.
          */}
          <div className={styles.head}>
            <h3 className={styles.title}>{t('substituteDayOff.title')}</h3>
            {canEdit && pendingDays.length > 0 && dayOffDraft === null ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  setDayOffDraft({ workDate: pendingDays[0]!.date, grantedOn: '', note: '' })
                }
              >
                {t('substituteDayOff.add')}
              </Button>
            ) : null}
          </div>
          <p className={styles.note}>{t('substituteDayOff.lead')}</p>

          {daysOff.data === undefined ? null : (
            <>
              {/* Dzień wolny wolno udzielić po zamknięciu okresu — ekran to nazywa (grupa 136). */}
              {daysOff.data.timePeriodStatus === 'closed' ? (
                <InfoBanner tone="info" className={styles.banner}>
                  {t('substituteDayOff.periodClosed')}
                </InfoBanner>
              ) : null}
              {/*
                Godziny bez dnia wolnego wchodzą na listę płac jako dodatek
                100 % (art. 151(11) § 2 i § 3) — ale dnia wolnego wolno jeszcze
                udzielić, dopóki termin nie minął.
              */}
              {daysOff.data.uncompensatedHours > 0 ? (
                <InfoBanner tone="warning" className={styles.banner}>
                  {t('substituteDayOff.uncompensated', {
                    hours: daysOff.data.uncompensatedHours,
                  })}
                </InfoBanner>
              ) : null}
              {/* Art. 151(12) k.p.: niedziela wolna co najmniej raz na cztery tygodnie. */}
              {daysOff.data.sundayStreakDate !== null ? (
                <InfoBanner tone="warning" className={styles.banner}>
                  {t('substituteDayOff.sundayStreak', {
                    date: formatDate(daysOff.data.sundayStreakDate, 'dot'),
                  })}
                </InfoBanner>
              ) : null}

              {daysOff.data.sundayDays.length === 0 && daysOff.data.freeDayWork.length === 0 ? (
                <EmptyState
                  title={t('substituteDayOff.empty')}
                  description={t('substituteDayOff.emptyBody')}
                />
              ) : (
                <Card pad="flush" className={styles.list}>
                  {[...daysOff.data.sundayDays, ...daysOff.data.freeDayWork].map((day) => {
                    const entry = daysOff.data.items.find((item) => item.workDate === day.date)
                    return (
                      <div key={day.date} className={styles.row}>
                        <span className={styles.rowName}>{formatDate(day.date, 'dot')}</span>
                        <span className={styles.rowMeta}>
                          {t(`substituteDayOff.reasons.${day.reason}`)}
                        </span>
                        <span className={styles.rowMeta}>
                          {t('substituteDayOff.hours', { hours: day.hours })}
                        </span>
                        <span className={styles.rowMeta}>
                          {day.grantedOn === null
                            ? t('substituteDayOff.missing')
                            : t(
                                day.inTerm
                                  ? 'substituteDayOff.granted'
                                  : 'substituteDayOff.grantedLate',
                                { date: formatDate(day.grantedOn, 'dot') },
                              )}
                        </span>
                        {canEdit && entry !== undefined ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={revokeDayOff.isPending}
                            onClick={() => revokeDayOff.mutate(entry.id)}
                          >
                            {t('substituteDayOff.revoke')}
                          </Button>
                        ) : null}
                      </div>
                    )
                  })}
                </Card>
              )}
            </>
          )}

          {dayOffDraft !== null ? (
            <>
              <div className={styles.formGrid}>
                <FormField
                  label={t('substituteDayOff.fields.workDate')}
                  htmlFor={ids.workDate}
                  hint={t('substituteDayOff.fields.workDateHint')}
                >
                  <Select
                    id={ids.workDate}
                    value={dayOffDraft.workDate}
                    onChange={(event) =>
                      setDayOffDraft({ ...dayOffDraft, workDate: event.target.value })
                    }
                  >
                    {pendingDays.map((day) => (
                      <option key={day.date} value={day.date}>
                        {`${formatDate(day.date, 'dot')} · ${t(
                          `substituteDayOff.reasons.${day.reason}`,
                        )}`}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField
                  label={t('substituteDayOff.fields.grantedOn')}
                  htmlFor={ids.grantedOn}
                  hint={t('substituteDayOff.fields.grantedOnHint')}
                >
                  <Input
                    id={ids.grantedOn}
                    type="date"
                    value={dayOffDraft.grantedOn}
                    onChange={(event) =>
                      setDayOffDraft({ ...dayOffDraft, grantedOn: event.target.value })
                    }
                  />
                </FormField>
                <FormField label={t('substituteDayOff.fields.note')} htmlFor={ids.dayOffNote}>
                  <Input
                    id={ids.dayOffNote}
                    value={dayOffDraft.note}
                    onChange={(event) =>
                      setDayOffDraft({ ...dayOffDraft, note: event.target.value })
                    }
                  />
                </FormField>
              </div>
              <div className={styles.actions}>
                <Button variant="secondary" onClick={() => setDayOffDraft(null)}>
                  {t('substituteDayOff.cancel')}
                </Button>
                <Button
                  disabled={
                    grantDayOff.isPending ||
                    dayOffDraft.workDate === '' ||
                    dayOffDraft.grantedOn === ''
                  }
                  onClick={() => {
                    setError(null)
                    grantDayOff.mutate(dayOffDraft)
                  }}
                >
                  {grantDayOff.isPending ? (
                    <Spinner label={t('substituteDayOff.saving')} />
                  ) : (
                    t('substituteDayOff.save')
                  )}
                </Button>
              </div>
            </>
          ) : null}

          {/*
            Rejestr dyżurów (art. 151(5) k.p., Etap 50). Dyżur nie jest ani
            czasem pracy, ani nadgodziną: czas wolny ma pierwszeństwo przed
            zapłatą, dyżur w domu nie daje nic, a pracownikowi zarządzającemu
            zakładem § 3 nie przysługuje (§ 4).
          */}
          <div className={styles.head}>
            <h3 className={styles.title}>{t('duty.title')}</h3>
            {canEdit && dutyDraft === null ? (
              <Button size="sm" variant="secondary" onClick={() => setDutyDraft(emptyDutyDraft())}>
                {t('duty.add')}
              </Button>
            ) : null}
          </div>
          <p className={styles.note}>{t('duty.lead')}</p>

          {duty.data === undefined ? null : (
            <>
              {/* Dyżur w zamkniętym okresie zapisze się dopiero po jego otwarciu (grupa 136). */}
              {duty.data.timePeriodStatus === 'closed' ? (
                <InfoBanner tone="warning" className={styles.banner}>
                  {t('duty.periodClosed')}
                </InfoBanner>
              ) : null}
              <p className={styles.note}>
                {t('duty.summary', {
                  standby: duty.data.standbyHours,
                  timeOff: duty.data.timeOffHours,
                  paid: duty.data.paidHours,
                })}
              </p>
              {/* Dyżur poza domem razem z pracą nie może zabrać 11 h odpoczynku. */}
              {/* Praca z dyżuru bez wpisu w ewidencji nie jest płacona jako praca (grupa 50). */}
              {duty.data.workNotRecordedHours > 0 ? (
                <InfoBanner tone="warning" className={styles.banner}>
                  {t('duty.workNotRecorded', { hours: duty.data.workNotRecordedHours })}
                </InfoBanner>
              ) : null}
              {duty.data.restShortageHours > 0 ? (
                <InfoBanner tone="warning" className={styles.banner}>
                  {t('duty.restWarning', { hours: duty.data.restShortageHours })}
                </InfoBanner>
              ) : null}

              {duty.data.items.length === 0 ? (
                <EmptyState title={t('duty.empty')} description={t('duty.emptyBody')} />
              ) : (
                <Card pad="flush" className={styles.list}>
                  {duty.data.items.map((item) => (
                    <div key={item.id} className={styles.row}>
                      <span className={styles.rowName}>{formatDate(item.date, 'dot')}</span>
                      <span className={styles.rowMeta}>{t(`duty.places.${item.place}`)}</span>
                      <span className={styles.rowMeta}>
                        {t('duty.hours', { hours: item.hours, worked: item.workedHours })}
                        {item.workNotRecordedHours > 0
                          ? ` · ${t('duty.shiftWorkNotRecorded', { hours: item.workNotRecordedHours })}`
                          : ''}
                      </span>
                      <span className={styles.rowMeta}>
                        {item.excluded === null
                          ? t('duty.compensation', {
                              timeOff: item.timeOffHours,
                              paid: item.paidHours,
                            })
                          : t(`duty.excluded.${item.excluded}`)}
                      </span>
                      {canEdit ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={revokeDuty.isPending}
                          onClick={() => revokeDuty.mutate(item.id)}
                        >
                          {t('duty.revoke')}
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </Card>
              )}
            </>
          )}

          {dutyDraft !== null ? (
            <>
              <div className={styles.formGrid}>
                <FormField label={t('duty.fields.date')} htmlFor={ids.dutyDate}>
                  <Input
                    id={ids.dutyDate}
                    type="date"
                    value={dutyDraft.date}
                    onChange={(event) => setDutyDraft({ ...dutyDraft, date: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('duty.fields.place')}
                  htmlFor={ids.dutyPlace}
                  hint={t('duty.fields.placeHint')}
                >
                  <Select
                    id={ids.dutyPlace}
                    value={dutyDraft.place}
                    onChange={(event) =>
                      setDutyDraft({
                        ...dutyDraft,
                        place: event.target.value as DutyDraft['place'],
                      })
                    }
                  >
                    <option value="zaklad">{t('duty.places.zaklad')}</option>
                    <option value="dom">{t('duty.places.dom')}</option>
                  </Select>
                </FormField>
                <FormField label={t('duty.fields.hours')} htmlFor={ids.dutyHours}>
                  <Input
                    id={ids.dutyHours}
                    value={dutyDraft.hours}
                    onChange={(event) => setDutyDraft({ ...dutyDraft, hours: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('duty.fields.workedHours')}
                  htmlFor={ids.dutyWorked}
                  hint={t('duty.fields.workedHoursHint')}
                >
                  <Input
                    id={ids.dutyWorked}
                    value={dutyDraft.workedHours}
                    onChange={(event) =>
                      setDutyDraft({ ...dutyDraft, workedHours: event.target.value })
                    }
                  />
                </FormField>
                <FormField
                  label={t('duty.fields.timeOffHours')}
                  htmlFor={ids.dutyTimeOff}
                  hint={t('duty.fields.timeOffHoursHint')}
                >
                  <Input
                    id={ids.dutyTimeOff}
                    value={dutyDraft.timeOffHours}
                    onChange={(event) =>
                      setDutyDraft({ ...dutyDraft, timeOffHours: event.target.value })
                    }
                  />
                </FormField>
                <FormField label={t('duty.fields.note')} htmlFor={ids.dutyNote}>
                  <Input
                    id={ids.dutyNote}
                    value={dutyDraft.note}
                    onChange={(event) => setDutyDraft({ ...dutyDraft, note: event.target.value })}
                  />
                </FormField>
              </div>
              <div className={styles.actions}>
                <Button variant="secondary" onClick={() => setDutyDraft(null)}>
                  {t('duty.cancel')}
                </Button>
                <Button
                  disabled={
                    recordDuty.isPending ||
                    dutyDraft.date === '' ||
                    Number(dutyDraft.hours.replace(',', '.')) <= 0 ||
                    /* Dzień w zamkniętym okresie serwer i tak odrzuci (409) — przycisk nie udaje, że zapisze (fala 9b). */
                    (dutyDraft.date.startsWith(month) && duty.data?.timePeriodStatus === 'closed')
                  }
                  onClick={() => {
                    setError(null)
                    recordDuty.mutate(dutyDraft)
                  }}
                >
                  {recordDuty.isPending ? <Spinner label={t('duty.saving')} /> : t('duty.save')}
                </Button>
              </div>
            </>
          ) : null}
        </>
      )}
    </section>
  )
}
